/**
 * Shared Module
 * 
 * Contains reusable components, pipes, directives, and services
 * that are used across multiple features in the application.
 * 
 * This module is imported by both AppModule and AgentsModule.
 */

import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { TranslateModule } from '@ngx-translate/core';
import { OverlayModule } from '@angular/cdk/overlay';

// Shared Components
import { SharedResponseRendererComponent } from './components/response-renderer/response-renderer.component';
import { MarkdownRendererComponent } from './components/markdown-renderer/markdown-renderer.component';
import { MarkdownRichTableComponent } from './components/markdown-rich-table/markdown-rich-table.component';
import { AgentFileAttachmentCardComponent } from './components/agent-file-attachment-card/agent-file-attachment-card.component';
import { ImageVisualizationComponent } from './components/image-visualization/image-visualization.component';
import { SessionHistoryComponent } from './components/session-history/session-history.component';
import { PromptSectionComponent } from './components/prompt-section/prompt-section.component';
import { RetrievalModeToggleComponent } from './components/retrieval-mode-toggle/retrieval-mode-toggle.component';
import { RetrievalModeHistoryNoticeComponent } from './components/retrieval-mode-history-notice/retrieval-mode-history-notice.component';
import { PromptLibraryComponent } from '../app/components/prompt-library/prompt-library.component';
import { DomainSwitchComponent } from '../app/components/domain-switch/domain-switch.component';
import { StreamingConfirmationPopupComponent } from './components/streaming-confirmation-popup/streaming-confirmation-popup.component';
import { AgentEngagingLoaderComponent } from './components/engaging-loader/engaging-loader.component';
import { DotsLoaderComponent } from './components/dots-loader/dots-loader.component';
import { RiaThinkingLoaderComponent } from './components/ria-thinking-loader/ria-thinking-loader.component';
import { AgentStreamingCursorComponent } from './components/agent-streaming-cursor/agent-streaming-cursor.component';
import { SharedResponseFeedbackComponent } from './components/response-feedback/response-feedback.component';
import { SharedResponseActionsComponent } from './components/response-actions/response-actions.component';
import { SharedResponseShareComponent } from './components/response-share/response-share.component';
import { SharedResponseOptionsMenuComponent } from './components/response-options-menu/response-options-menu.component';

// Components from app used by shared components
import { ChartComponent } from '../app/components/chart/chart.component';
import { LoadingComponent } from '../app/components/loading/loading.component';
import { VoiceInputComponent } from '../app/components/voice-input/voice-input.component';
import { EngagingLoaderComponent } from '../app/components/engaging-loader/engaging-loader.component';

// Shared Pipes
import { MarkdownSanitizerPipe } from './pipes/markdown-sanitizer.pipe/markdown-sanitizer.pipe';

// Directives (same contenteditable behavior as chatbot textarea)
import { ContenteditableModelDirective } from '../directives/contenteditable-textarea/contenteditable-model.directive';

import { LoginFormComponent } from './components/login-form/login-form.component';


@NgModule({
  declarations: [
    // Directives
    ContenteditableModelDirective,
    // Shared Components
    SharedResponseRendererComponent,
    MarkdownRendererComponent,
    MarkdownRichTableComponent,
    AgentFileAttachmentCardComponent,
    ImageVisualizationComponent,
    SessionHistoryComponent,
    PromptSectionComponent,
    RetrievalModeToggleComponent,
    RetrievalModeHistoryNoticeComponent,
    PromptLibraryComponent,
    DomainSwitchComponent,
    StreamingConfirmationPopupComponent,
    AgentEngagingLoaderComponent,
    DotsLoaderComponent,
    RiaThinkingLoaderComponent,
    AgentStreamingCursorComponent,
    SharedResponseFeedbackComponent,
    SharedResponseActionsComponent,
    SharedResponseShareComponent,
    SharedResponseOptionsMenuComponent,
    ChartComponent,
    LoadingComponent,
    VoiceInputComponent,
    EngagingLoaderComponent,

    // Shared Pipes
    MarkdownSanitizerPipe,
    LoginFormComponent
  ],
  imports: [
    CommonModule,
    FormsModule,
    HttpClientModule,
    TranslateModule,
    OverlayModule
  ],
  exports: [
    // Export all shared components, pipes, and directives
    // so they can be used in any module that imports SharedModule
    ContenteditableModelDirective,
    SharedResponseRendererComponent,
    MarkdownRendererComponent,
    MarkdownRichTableComponent,
    AgentFileAttachmentCardComponent,
    ImageVisualizationComponent,
    SessionHistoryComponent,
    PromptSectionComponent,
    RetrievalModeToggleComponent,
    RetrievalModeHistoryNoticeComponent,
    PromptLibraryComponent,
    DomainSwitchComponent,
    StreamingConfirmationPopupComponent,
    AgentEngagingLoaderComponent,
    DotsLoaderComponent,
    RiaThinkingLoaderComponent,
    AgentStreamingCursorComponent,
    SharedResponseFeedbackComponent,
    SharedResponseActionsComponent,
    SharedResponseShareComponent,
    SharedResponseOptionsMenuComponent,
    ChartComponent,
    LoadingComponent,
    VoiceInputComponent,
    EngagingLoaderComponent,
    MarkdownSanitizerPipe,
    LoginFormComponent
  ],
  providers: [
    // Shared services are already providedIn: 'root', but listed here for documentation
    // ChatAutoScrollService
  ]
})
export class SharedModule { }

